/**
 * Color Harmony Utilities
 * Shared logic for calculating and working with color harmonies
 */

import { colorEngine } from '../colorEngine';

export type HarmonyType = 'complementary' | 'analogous' | 'triadic' | 'split-complementary' | 'tetradic' | 'monochromatic';

export interface HarmonyScheme {
  type: HarmonyType;
  name: string;
  description: string;
  baseColor: string;
  colors: string[];
  angles: number[];
  metadata: {
    contrast: 'high' | 'medium' | 'low';
    vibrancy: 'high' | 'medium' | 'low';
    mood: string[];
  };
}

export interface HarmonyVariation {
  colors: string[];
  saturation: number;
  lightness: number;
  temperature: 'warm' | 'cool' | 'neutral';
}

/**
 * Calculate color harmony based on base color and harmony type
 */
export function calculateHarmony(baseColor: string, type: HarmonyType): HarmonyScheme {
  const parsedColor = colorEngine.parseColor(baseColor);
  if (!parsedColor?.hsl) {
    throw new Error(`Invalid color: ${baseColor}`);
  }

  const { h: hue, s: saturation, l: lightness } = parsedColor.hsl;
  
  const harmonies: Record<HarmonyType, Omit<HarmonyScheme, 'type' | 'baseColor'>> = {
    complementary: {
      name: 'Complementary',
      description: 'Colors opposite each other create maximum contrast and visual tension',
      colors: [
        baseColor,
        `hsl(${(hue + 180) % 360}, ${saturation}%, ${lightness}%)`
      ],
      angles: [0, 180],
      metadata: {
        contrast: 'high',
        vibrancy: 'high',
        mood: ['energetic', 'bold', 'dynamic']
      }
    },
    analogous: {
      name: 'Analogous',
      description: 'Adjacent colors create harmonious, serene combinations',
      colors: [
        `hsl(${(hue - 30 + 360) % 360}, ${saturation}%, ${lightness}%)`,
        baseColor,
        `hsl(${(hue + 30) % 360}, ${saturation}%, ${lightness}%)`
      ],
      angles: [-30, 0, 30],
      metadata: {
        contrast: 'low',
        vibrancy: 'medium',
        mood: ['calm', 'comfortable', 'unified']
      }
    },
    triadic: {
      name: 'Triadic',
      description: 'Three evenly spaced colors create vibrant, balanced schemes',
      colors: [
        baseColor,
        `hsl(${(hue + 120) % 360}, ${saturation}%, ${lightness}%)`,
        `hsl(${(hue + 240) % 360}, ${saturation}%, ${lightness}%)`
      ],
      angles: [0, 120, 240],
      metadata: {
        contrast: 'medium',
        vibrancy: 'high',
        mood: ['balanced', 'energetic', 'playful']
      }
    },
    'split-complementary': {
      name: 'Split Complementary',
      description: 'Base color with two adjacent to its complement for strong but harmonious contrast',
      colors: [
        baseColor,
        `hsl(${(hue + 150) % 360}, ${saturation}%, ${lightness}%)`,
        `hsl(${(hue + 210) % 360}, ${saturation}%, ${lightness}%)`
      ],
      angles: [0, 150, 210],
      metadata: {
        contrast: 'high',
        vibrancy: 'medium',
        mood: ['sophisticated', 'dynamic', 'balanced']
      }
    },
    tetradic: {
      name: 'Tetradic',
      description: 'Four colors in two complementary pairs offer rich variety',
      colors: [
        baseColor,
        `hsl(${(hue + 90) % 360}, ${saturation}%, ${lightness}%)`,
        `hsl(${(hue + 180) % 360}, ${saturation}%, ${lightness}%)`,
        `hsl(${(hue + 270) % 360}, ${saturation}%, ${lightness}%)`
      ],
      angles: [0, 90, 180, 270],
      metadata: {
        contrast: 'high',
        vibrancy: 'high',
        mood: ['rich', 'varied', 'complex']
      }
    },
    monochromatic: {
      name: 'Monochromatic',
      description: 'Variations of a single hue create sophisticated, unified schemes',
      colors: [
        `hsl(${hue}, ${saturation}%, ${Math.max(20, lightness - 20)}%)`,
        `hsl(${hue}, ${saturation}%, ${lightness}%)`,
        `hsl(${hue}, ${saturation}%, ${Math.min(80, lightness + 20)}%)`
      ],
      angles: [0, 0, 0],
      metadata: {
        contrast: 'low',
        vibrancy: 'low',
        mood: ['elegant', 'unified', 'sophisticated']
      }
    }
  };

  return {
    type,
    baseColor,
    ...harmonies[type]
  };
}

/**
 * Generate variations of a harmony scheme
 */
export function generateHarmonyVariations(scheme: HarmonyScheme): HarmonyVariation[] {
  const variations: HarmonyVariation[] = [];
  
  // Original
  variations.push({
    colors: scheme.colors,
    saturation: 100,
    lightness: 50,
    temperature: getTemperature(scheme.baseColor)
  });

  // Muted version
  variations.push({
    colors: scheme.colors.map(color => {
      const parsed = colorEngine.parseColor(color);
      if (parsed?.hsl) {
        return `hsl(${parsed.hsl.h}, ${parsed.hsl.s * 0.6}%, ${parsed.hsl.l}%)`;
      }
      return color;
    }),
    saturation: 60,
    lightness: 50,
    temperature: getTemperature(scheme.baseColor)
  });

  // Pastel version
  variations.push({
    colors: scheme.colors.map(color => {
      const parsed = colorEngine.parseColor(color);
      if (parsed?.hsl) {
        return `hsl(${parsed.hsl.h}, ${parsed.hsl.s * 0.5}%, ${Math.min(85, parsed.hsl.l + 15)}%)`;
      }
      return color;
    }),
    saturation: 50,
    lightness: 70,
    temperature: getTemperature(scheme.baseColor)
  });

  // Dark version
  variations.push({
    colors: scheme.colors.map(color => {
      const parsed = colorEngine.parseColor(color);
      if (parsed?.hsl) {
        return `hsl(${parsed.hsl.h}, ${parsed.hsl.s}%, ${Math.max(25, parsed.hsl.l - 25)}%)`;
      }
      return color;
    }),
    saturation: 100,
    lightness: 30,
    temperature: getTemperature(scheme.baseColor)
  });

  return variations;
}

/**
 * Check if colors form a specific harmony type
 */
export function checkHarmonyType(colors: string[], targetType: HarmonyType): boolean {
  if (colors.length < 2) return false;

  const hues = colors
    .map(color => colorEngine.parseColor(color)?.hsl?.h)
    .filter((h): h is number => h !== undefined)
    .sort((a, b) => a - b);

  if (hues.length < 2) return false;

  switch (targetType) {
    case 'complementary': {
      if (hues.length !== 2) return false;
      const diff = Math.abs(hues[1] - hues[0]);
      return diff >= 150 && diff <= 210;
    }
    
    case 'analogous': {
      return hues.every((hue, i) => {
        if (i === 0) return true;
        const diff = Math.abs(hue - hues[i - 1]);
        return diff <= 60;
      });
    }
    
    case 'triadic': {
      if (hues.length !== 3) return false;
      const diffs = [
        Math.abs(hues[1] - hues[0]),
        Math.abs(hues[2] - hues[1]),
        Math.abs((hues[0] + 360) - hues[2])
      ];
      return diffs.every(diff => diff >= 100 && diff <= 140);
    }
    
    case 'split-complementary': {
      if (hues.length !== 3) return false;
      // Find the base color and check if others are ±30° from its complement
      const baseHue = hues[1]; // Middle hue is likely the base
      const complement = (baseHue + 180) % 360;
      return hues.some(h => Math.abs(h - complement) <= 30);
    }
    
    case 'tetradic': {
      if (hues.length !== 4) return false;
      // Check for two complementary pairs
      const pairs = [
        [hues[0], hues[2]],
        [hues[1], hues[3]]
      ];
      return pairs.every(([h1, h2]) => {
        const diff = Math.abs(h2 - h1);
        return diff >= 170 && diff <= 190;
      });
    }
    
    case 'monochromatic': {
      const baseHue = hues[0];
      return hues.every(h => {
        const diff = Math.abs(h - baseHue);
        return diff <= 15 || diff >= 345; // Allow small variations
      });
    }
    
    default:
      return false;
  }
}

/**
 * Get the temperature of a color
 */
export function getTemperature(color: string): 'warm' | 'cool' | 'neutral' {
  const parsed = colorEngine.parseColor(color);
  if (!parsed?.hsl) return 'neutral';

  const { h } = parsed.hsl;
  
  if ((h >= 0 && h <= 60) || (h >= 300 && h <= 360)) {
    return 'warm';
  } else if ((h >= 180 && h <= 240)) {
    return 'cool';
  } else {
    return 'neutral';
  }
}

/**
 * Generate color scheme variations with different saturation and lightness
 */
export function adjustSchemeColors(
  colors: string[],
  saturationFactor: number = 1,
  lightnessAdjust: number = 0
): string[] {
  return colors.map(color => {
    const parsed = colorEngine.parseColor(color);
    if (parsed?.hsl) {
      const newSaturation = Math.max(0, Math.min(100, parsed.hsl.s * saturationFactor));
      const newLightness = Math.max(0, Math.min(100, parsed.hsl.l + lightnessAdjust));
      return `hsl(${parsed.hsl.h}, ${newSaturation}%, ${newLightness}%)`;
    }
    return color;
  });
}

/**
 * Export harmony scheme as CSS variables
 */
export function exportSchemeAsCSS(scheme: HarmonyScheme, prefix: string = 'color'): string {
  const cssVars = scheme.colors.map((color, index) => {
    return `  --${prefix}-${index + 1}: ${color};`;
  }).join('\n');

  return `:root {\n${cssVars}\n}`;
}

/**
 * Export harmony scheme as JSON
 */
export function exportSchemeAsJSON(scheme: HarmonyScheme): string {
  return JSON.stringify(scheme, null, 2);
}
